import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import AdminSidebar from "./AdminSidebar";
import AdminNavbar from "./AdminNavbar";

// Import admin pages
import Dashboard from "../pages/Dashboard";
import CreateStateAdmin from "../pages/AdminRolesManagement";
import AdminRolesManagement from "../pages/AdminRolesManagement";
import CreateCustomRole from "../pages/CreateCustomRole";
import CreateGroupChat from "../chat/CreateGroupChat";
import GroupChatPage from "../pages/GroupChatPage";
import ViewMembers from "../chat/ViewMembers";
import ViewAdmins from "../chat/ViewAdmins";
import UserDetailView from "../chat/UserDetailView";
import ManageBrandIdentity from "../brand/ManageBrandIdentity";
import BroadcastMessage from "../chat/BroadcastMessage";
import FullReportAccess from "../pages/FullReportAccess";

// Import different admin dashboards
import StateDashboard from "../state/StateDashboard";
import LGADashboard from "../pages/LGADashboard";
import DistrictDashboard from "../pages/DistrictDashboard";
import SenatorialDashboard from "../senitorial/SenatorialDistrictDashboard";
import PollingUnitDashboard from "../pages/PollingUnitDashboard";
import FCDashboard from "../fc/FcDashboard"; // Import FC Dashboard

// Import Admin Profile
import AdminProfile from "../pages/AdminProfile";

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, hasAnyPermission, hasPermission } = useAuth();

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Role-based access control
  const canAccessRoute = (requiredRoles = []) => {
    if (!user || !user.role) return false;
    return requiredRoles.includes(user.role);
  };

  // Check if admin can create admins based on role
  const canCreateAdmins = () => {
    if (user?.role === "super-admin") return true;
    if (user?.role === "state-admin") {
      return hasAnyPermission(["create-sd-admin", "create-fc-admin", "create-lga-admin", "create-lcda-admin"]);
    }
    if (user?.role === "sd-admin") {
      return hasAnyPermission(["create-fc-admin", "create-lga-admin", "create-lcda-admin"]);
    }
    if (user?.role === "fc-admin") {
      return hasAnyPermission(["create-lcda-admin", "create-ward-admin"]);
    }
    if (user?.role === "lga-admin") {
      return hasAnyPermission(["create-lcda-admin", "create-ward-admin"]);
    }
    if (user?.role === "lcda-admin") {
      return hasPermission("create-ward-admin");
    }
    if (user?.role === "ward-admin") {
      return hasPermission("create-polling-unit-admin");
    }
    return false;
  };

  // Check if admin can view other admins
  const canViewAdmins = () => {
    if (user?.role === "super-admin") return true;
    if (user?.role === "state-admin") {
      return hasAnyPermission(["create-sd-admin", "create-fc-admin", "create-lga-admin", "create-lcda-admin"]);
    }
    if (user?.role === "sd-admin") {
      return hasAnyPermission(["create-fc-admin", "create-lga-admin", "create-lcda-admin"]);
    }
    if (["fc-admin", "lga-admin", "lcda-admin", "ward-admin"].includes(user?.role)) {
      return canCreateAdmins();
    }
    return false;
  };

  // Check if admin can broadcast messages
  const canBroadcast = () => {
    if (!user?.role) return false;
    
    const broadcastPermissions = {
      "super-admin": ["broadcast-announcement-nationwide", "broadcast-request-nationwide"],
      "state-admin": ["broadcast-announcement-state", "broadcast-request-state"],
      "sd-admin": ["broadcast-announcement-senatorial-district", "broadcast-request-senatorial-district"],
      "fc-admin": ["broadcast-announcement-federal-constituency", "broadcast-request-federal-constituency"],
      "lga-admin": ["broadcast-announcement-local-government", "broadcast-request-local-government"],
      "lcda-admin": ["broadcast-announcement-lcda", "broadcast-request-lcda"],
      "ward-admin": ["broadcast-announcement-ward", "broadcast-request-ward"],
      "polling-unit-admin": ["broadcast-announcement-polling-unit", "broadcast-request-polling-unit"]
    };

    const permissions = broadcastPermissions[user.role] || [];
    return hasAnyPermission(permissions);
  };

  // Check if admin can create group chats
  const canCreateGroupChats = () => {
    if (!user?.role) return false;
    
    const groupChatPermissions = {
      "super-admin": ["create-group-chat-nationwide"],
      "state-admin": ["create-group-chat-state"],
      "sd-admin": ["create-group-chat-senatorial-district"],
      "fc-admin": ["create-group-chat-federal-constituency"],
      "lga-admin": ["create-group-chat-local-government"],
      "lcda-admin": ["create-group-chat-lcda"],
      "ward-admin": ["create-group-chat-ward"],
      "polling-unit-admin": ["create-group-chat-polling-unit"]
    };

    const permissions = groupChatPermissions[user.role] || [];
    return hasAnyPermission(permissions);
  };

  // Check if admin can view reports
  const canViewReports = () => {
    if (!user?.role) return false;
    
    const reportPermissions = {
      "super-admin": ["view-reports", "view-nationwide-reports"],
      "state-admin": ["view-reports", "view-state-reports"],
      "sd-admin": ["view-reports", "view-sd-reports"],
      "fc-admin": ["view-reports", "view-fc-reports"],
      "lga-admin": ["view-reports", "view-lga-reports"],
      "lcda-admin": ["view-reports", "view-lcda-reports"],
      "ward-admin": ["view-reports", "view-ward-reports"],
      "polling-unit-admin": ["view-reports", "view-polling-unit-reports"]
    };

    const permissions = reportPermissions[user.role] || [];
    return hasAnyPermission(permissions);
  };

  // Check if admin can manage members
  const canManageMembers = () => {
    return hasAnyPermission([
      "view-members", "create-members", "edit-members", "delete-members",
      "approve-members", "suspend-members", "reject-members", "reactivate-members"
    ]);
  };

  // Get default dashboard based on role
  const getDefaultDashboard = () => {
    if (!user || !user.role) return "/admin/dashboard";

    switch (user.role) {
      case "state-admin":
        return "/admin/state-dashboard";
      case "sd-admin":
        return "/admin/senatorial-dashboard";
      case "fc-admin":
        return "/admin/federal-constituency-dashboard";
      case "lga-admin":
        return "/admin/lga-dashboard";
      case "lcda-admin":
        return "/admin/lcda-dashboard";
      case "ward-admin":
        return "/admin/ward-dashboard";
      case "polling-unit-admin":
        return "/admin/polling-unit-dashboard";
      case "super-admin":
      default:
        return "/admin/dashboard";
    }
  };

  const layoutStyle = {
    display: "flex",
    minHeight: "100vh",
    backgroundColor: "#f8f9fa",
  };

  const mainContentStyle = {
    flex: 1,
    marginLeft: isMobile ? "0" : "250px",
    transition: "margin-left 0.3s ease",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  };

  const contentStyle = {
    flex: 1,
    padding: "20px",
    minHeight: "calc(100vh - 70px)",
    overflowX: "auto",
  };

  // Access Denied Component
  const AccessDenied = ({
    message = "You don't have permission to access this page.",
  }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "50vh",
        flexDirection: "column",
        textAlign: "center",
      }}
    >
      <h2 style={{ color: "#dc3545", marginBottom: "1rem" }}>Access Denied</h2>
      <p style={{ marginBottom: "2rem", color: "#6c757d" }}>{message}</p>
      <button
        onClick={() => window.history.back()}
        style={{
          padding: "10px 20px",
          backgroundColor: "#007bff",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      >
        Go Back
      </button>
    </div>
  );

  return (
    <div style={layoutStyle}>
      <AdminSidebar
        isOpen={sidebarOpen}
        onClose={closeSidebar}
        userRole={user?.role}
      />

      <div style={mainContentStyle}>
        <AdminNavbar onMenuToggle={toggleSidebar} userRole={user?.role} />
        <div style={contentStyle}>
          <Routes>
            {/* Default redirect based on role */}
            <Route
              path="/"
              element={<Navigate to={getDefaultDashboard()} replace />}
            />

            {/* Profile Route - Accessible to all admins */}
            <Route path="/profile" element={<AdminProfile />} />

            {/* Create Admin Route - For admins who can create lower-level admins */}
            <Route
              path="/create-admin"
              element={
                canCreateAdmins() ? (
                  <CreateStateAdmin />
                ) : (
                  <AccessDenied message="You don't have permission to create admins." />
                )
              }
            />

            {/* Roles Management Route - For super-admin AND state-admin */}
            <Route
              path="/roles-management"
              element={
                canAccessRoute(["super-admin", "state-admin"]) ? (
                  <AdminRolesManagement />
                ) : (
                  <AccessDenied message="Only Super Administrators and State Administrators can manage roles and permissions." />
                )
              }
            />

            {/* Create Custom Role Route - ONLY for super-admin */}
            <Route
              path="/create-custom-role"
              element={
                canAccessRoute(["super-admin"]) ? (
                  <CreateCustomRole />
                ) : (
                  <AccessDenied message="Only Super Administrators can create custom roles." />
                )
              }
            />

            {/* View Admins Route - For admins who can view other admins */}
            <Route
              path="/view-admins"
              element={
                canViewAdmins() ? (
                  <ViewAdmins />
                ) : (
                  <AccessDenied message="You don't have permission to view administrators." />
                )
              }
            />

            {/* Super Admin Dashboard - ONLY for super-admin */}
            <Route
              path="/dashboard"
              element={
                canAccessRoute(["super-admin"]) ? (
                  <Dashboard />
                ) : (
                  <AccessDenied message="Only Super Administrators can access this dashboard." />
                )
              }
            />

            {/* State Admin Dashboard - ONLY for state-admin */}
            <Route
              path="/state-dashboard"
              element={
                canAccessRoute(["state-admin"]) ? (
                  <StateDashboard />
                ) : (
                  <AccessDenied message="Only State Administrators can access this dashboard." />
                )
              }
            />

            {/* Senatorial District Admin Dashboard - ONLY for sd-admin */}
            <Route
              path="/senatorial-dashboard"
              element={
                canAccessRoute(["sd-admin"]) ? (
                  <SenatorialDashboard />
                ) : (
                  <AccessDenied message="Only Senatorial District Administrators can access this dashboard." />
                )
              }
            />

            {/* Federal Constituency Admin Dashboard - ONLY for fc-admin */}
            <Route
              path="/federal-constituency-dashboard"
              element={
                canAccessRoute(["fc-admin"]) ? (
                  <FCDashboard />
                ) : (
                  <AccessDenied message="Only Federal Constituency Administrators can access this dashboard." />
                )
              }
            />

            {/* LGA Admin Dashboard - ONLY for lga-admin */}
            <Route
              path="/lga-dashboard"
              element={
                canAccessRoute(["lga-admin"]) ? (
                  <LGADashboard />
                ) : (
                  <AccessDenied message="Only LGA Administrators can access this dashboard." />
                )
              }
            />

            {/* LCDA Admin Dashboard - ONLY for lcda-admin */}
            <Route
              path="/lcda-dashboard"
              element={
                canAccessRoute(["lcda-admin"]) ? (
                  <DistrictDashboard />
                ) : (
                  <AccessDenied message="Only LCDA Administrators can access this dashboard." />
                )
              }
            />

            {/* Ward Admin Dashboard - ONLY for ward-admin */}
            <Route
              path="/ward-dashboard"
              element={
                canAccessRoute(["ward-admin"]) ? (
                  <DistrictDashboard />
                ) : (
                  <AccessDenied message="Only Ward Administrators can access this dashboard." />
                )
              }
            />

            {/* Polling Unit Admin Dashboard - ONLY for polling-unit-admin */}
            <Route
              path="/polling-unit-dashboard"
              element={
                canAccessRoute(["polling-unit-admin"]) ? (
                  <PollingUnitDashboard />
                ) : (
                  <AccessDenied message="Only Polling Unit Administrators can access this dashboard." />
                )
              }
            />

            {/* Group Chat Routes */}
            <Route
              path="/create-group-chat"
              element={
                canCreateGroupChats() ? (
                  <CreateGroupChat />
                ) : (
                  <AccessDenied message="You don't have permission to create group chats." />
                )
              }
            />
            <Route 
              path="/create-group-chat/:id" 
              element={
                canCreateGroupChats() ? (
                  <GroupChatPage />
                ) : (
                  <AccessDenied message="You don't have permission to access group chats." />
                )
              } 
            />

            {/* View Members Route */}
            <Route
              path="/view-members"
              element={
                canManageMembers() ? (
                  <ViewMembers />
                ) : (
                  <AccessDenied message="You don't have permission to view members." />
                )
              }
            />

            {/* User Detail View Route */}
            <Route
              path="/user-details/:userId"
              element={
                canManageMembers() ? (
                  <UserDetailView />
                ) : (
                  <AccessDenied message="You don't have permission to view user details." />
                )
              }
            />

            {/* Brand Management - ONLY for super-admin */}
            <Route
              path="/manage-brand"
              element={
                canAccessRoute(["super-admin"]) ? (
                  <ManageBrandIdentity />
                ) : (
                  <AccessDenied message="Only Super Administrators can manage brand identity." />
                )
              }
            />

            {/* Broadcast Route */}
            <Route
              path="/broadcast"
              element={
                canBroadcast() ? (
                  <BroadcastMessage />
                ) : (
                  <AccessDenied message="You don't have permission to broadcast messages." />
                )
              }
            />

            {/* Reports Route */}
            <Route
              path="/reports"
              element={
                canViewReports() ? (
                  <FullReportAccess />
                ) : (
                  <AccessDenied message="You don't have permission to access reports." />
                )
              }
            />

            {/* Catch all route - redirect to appropriate dashboard */}
            <Route path="*" element={<Navigate to={getDefaultDashboard()} replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;